// apps/web/src/components/visitors/VisitorCard/VisitorStatusChips.tsx
import React from 'react';
import { Chip, Box, Typography, LinearProgress, SxProps, Theme } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  CardMembership as CardMembershipIcon,
  Event as EventIcon,
  Block as BlockIcon,
  PersonOutline as PersonOutlineIcon,
} from '@mui/icons-material';
import { format, isPast } from 'date-fns';
import { ru } from 'date-fns/locale';

interface VisitorStatusChipsProps {
  hasActiveSubscription?: boolean;
  isActive?: boolean;
  isCheckedIn?: boolean;
  variant?: 'compact' | 'normal' | 'full';
  sx?: SxProps<Theme>;
  subscriptionName?: string;
  remaining?: number;
  subscriptionType?: 'period' | 'visits';
  totalCount?: number;
  endDate?: string;
}

const getDaysWord = (count: number): string => {
  if (count % 10 === 1 && count % 100 !== 11) return 'день';
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'дня';
  return 'дней';
};

const getVisitsWord = (count: number): string => {
  if (count % 10 === 1 && count % 100 !== 11) return 'посещение';
  if ([2, 3, 4].includes(count % 10) && ![12, 13, 14].includes(count % 100)) return 'посещения';
  return 'посещений';
};

const VisitorStatusChips: React.FC<VisitorStatusChipsProps> = ({
  hasActiveSubscription,
  isActive = true,
  isCheckedIn = false,
  variant = 'normal',
  sx,
  subscriptionName,
  remaining,
  subscriptionType,
  totalCount,
  endDate,
}) => {
  const showSubscription = hasActiveSubscription !== undefined ? hasActiveSubscription : isActive;

  const progress = React.useMemo(() => {
    if (!showSubscription || remaining === undefined || !totalCount) return null;
    return (remaining / totalCount) * 100;
  }, [showSubscription, remaining, totalCount]);

  const progressColor = React.useMemo(() => {
    if (!showSubscription || remaining === undefined || !totalCount) return 'success';
    if (remaining === 0) return 'error';
    const percentRemaining = (remaining / totalCount) * 100;
    if (percentRemaining <= 10) return 'error';
    if (percentRemaining <= 30) return 'warning';
    return 'success';
  }, [showSubscription, remaining, totalCount]);

  const subscriptionStatusText = React.useMemo(() => {
    if (!showSubscription || !endDate) return null;
    const date = new Date(endDate);
    const formattedDate = format(date, 'd MMM yyyy', { locale: ru });
    if (isPast(date)) {
      return `Истёк ${formattedDate}`;
    }
    if (remaining === undefined) return `до ${formattedDate}`;
    const word = subscriptionType === 'period' ? getDaysWord(remaining) : getVisitsWord(remaining);
    return `${remaining} ${word} до ${formattedDate}`;
  }, [showSubscription, endDate, remaining, subscriptionType]);

  const sizes = {
    compact: {
      chipSize: { height: 24, fontSize: '0.75rem' },
      iconSize: 16,
      progressHeight: 3,
      spacing: 0.75,
    },
    normal: {
      chipSize: { height: 28, fontSize: '0.8rem' },
      iconSize: 18,
      progressHeight: 5,
      spacing: 1,
    },
    full: {
      chipSize: { height: 32, fontSize: '0.85rem' },
      iconSize: 20,
      progressHeight: 8,
      spacing: 1.25,
    },
  };

  const current = sizes[variant];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: current.spacing,
        width: '100%',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', gap: current.spacing, flexWrap: 'wrap', alignItems: 'center' }}>
        {!isActive && (
          <Chip
            icon={<BlockIcon sx={{ fontSize: current.iconSize }} />}
            label="Неактивен"
            sx={{
              ...current.chipSize,
              fontWeight: 500,
              backgroundColor: '#000000 !important',
              color: '#ffffff !important',
              '& .MuiChip-icon': {
                ml: 0.5,
                mr: -0.25,
                color: '#ffffff !important',
              },
              '& .MuiChip-label': { px: 1 },
            }}
          />
        )}

        {isActive && isCheckedIn && (
          <Chip
            icon={<CheckCircleIcon sx={{ fontSize: current.iconSize }} />}
            label="В зале"
            color="primary"
            size="small"
            sx={{
              ...current.chipSize,
              fontWeight: 500,
              '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
              '& .MuiChip-label': { px: 1 },
            }}
          />
        )}
        {isActive && !showSubscription && (
          <Chip
            icon={<PersonOutlineIcon sx={{ fontSize: current.iconSize }} />}
            label="Без абонемента"
            color="default"
            size="small"
            sx={{
              ...current.chipSize,
              fontWeight: 500,
              '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
              '& .MuiChip-label': { px: 1 },
            }}
          />
        )}

        {showSubscription && (
          <Chip
            icon={<CardMembershipIcon sx={{ fontSize: current.iconSize }} />}
            label={subscriptionName || 'Абонемент'}
            color="success"
            size="small"
            sx={{
              ...current.chipSize,
              fontWeight: 500,
              '& .MuiChip-icon': { ml: 0.5, mr: -0.25 },
              '& .MuiChip-label': { px: 1 },
            }}
          />
        )}
      </Box>

      {isActive && showSubscription && subscriptionStatusText && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <EventIcon
            sx={{ fontSize: current.iconSize - 2, color: 'text.secondary', flexShrink: 0 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: current.chipSize.fontSize,
              lineHeight: 1.3,
            }}
          >
            {subscriptionStatusText}
          </Typography>
        </Box>
      )}

      {isActive && progress !== null && (
        <LinearProgress
          variant="determinate"
          value={progress}
          color={progressColor}
          sx={{ height: current.progressHeight, borderRadius: current.progressHeight / 2 }}
        />
      )}
    </Box>
  );
};

export default VisitorStatusChips;
